import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { MailCheck, MailWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";

// /verify-email?token=… — consumes a single-use verification link.
export default function VerifyEmail() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [state, setState] = useState(token ? "working" : "missing");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) return;
    api.post("/auth/verify-email", { token })
      .then((r) => { setState("ok"); setMsg(r.data.message); if (user) setUser({ ...user, email_verified: true }); })
      .catch((e) => { setState("error"); setMsg(e?.response?.data?.detail || "This verification link is invalid or has expired."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F9FE] px-4">
      <Card className="w-full max-w-md p-8 bg-white border-slate-200 shadow-lg text-center">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6"><Logo size={44} /><span className="font-display font-extrabold text-[#0A2A5E] text-lg">LUMIS HR</span></Link>
        {state === "working" && <p data-testid="verify-working" className="text-sm text-slate-600 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your email…</p>}
        {state === "ok" && <div data-testid="verify-ok"><MailCheck className="w-10 h-10 text-emerald-600 mx-auto" /><h1 className="font-display text-xl font-bold text-[#0A2A5E] mt-3">Email verified</h1><p className="text-sm text-slate-600 mt-2">{msg}</p><Button data-testid="verify-continue" onClick={() => nav(user ? "/app/chat" : "/login")} className="mt-5 bg-[#0A2A5E] text-white">Continue</Button></div>}
        {(state === "error" || state === "missing") && <div data-testid="verify-error"><MailWarning className="w-10 h-10 text-rose-600 mx-auto" /><h1 className="font-display text-xl font-bold text-[#0A2A5E] mt-3">Link not valid</h1><p className="text-sm text-slate-600 mt-2">{msg || "This verification link is incomplete."}</p><p className="text-xs text-slate-500 mt-3">Sign in and use “Resend verification email” to get a new link.</p><Button variant="outline" onClick={() => nav("/login")} className="mt-4">Go to sign in</Button></div>}
      </Card>
    </div>
  );
}

// Blocking gate shown inside the app until a new email/password account verifies its address.
export function VerifyGate() {
  const { user, setUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/resend-verification"); toast.success(data.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend right now"); }
    finally { setBusy(false); }
  };
  const recheck = async () => { const { data } = await api.get("/auth/me"); setUser(data); if (!data.email_verified) toast("Not verified yet — check your inbox."); };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F9FE] px-4">
      <Card data-testid="verify-gate" className="w-full max-w-md p-8 bg-white border-slate-200 shadow-lg text-center">
        <Logo size={48} className="mx-auto" />
        <h1 className="font-display text-xl font-bold text-[#0A2A5E] mt-4">Verify your email to continue</h1>
        <p className="text-sm text-slate-600 mt-2">We sent a verification link to <strong>{user?.email}</strong>. The link is valid for 24 hours and can be used once. Full access opens as soon as you confirm.</p>
        <div className="mt-5 flex flex-col gap-2">
          <Button data-testid="verify-resend" onClick={resend} disabled={busy} className="bg-[#0A2A5E] text-white">{busy ? "Sending…" : "Resend verification email"}</Button>
          <Button data-testid="verify-recheck" variant="outline" onClick={recheck}>I've verified — refresh</Button>
          <button type="button" data-testid="verify-logout" onClick={logout} className="text-xs text-slate-500 hover:underline mt-2">Sign out</button>
        </div>
      </Card>
    </div>
  );
}
